import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { TextField, Grid, Divider } from '@mic3/platform-ui';

import SelectionList from 'app/containers/Common/SelectionList/SelectionList';
import { bind, memoize, debounce } from 'app/utils/decorators/decoratorUtils';
import { getStr, isEmpty } from 'app/utils/utils';
import { addWorkspaceTeams, addWorkspaceUsers, removeWorkspaceTeams, removeWorkspaceUsers, loadWorkspaceResources, loadWorkspaceUsers } from 'store/actions/admin/workspacesActions';

const StyledGrid = styled(Grid)`
    margin: 0 8px;
    position: sticky !important;
    top: 0;
    background: ${({ theme }) => theme.material.colors.background.default} !important;
    z-index: 999;
`;

const SearchField = styled(TextField)`
    .MuiFilledInput-input {
        padding: 10px 12px 10px 0 !important;
        background: ${({ theme }) => theme.base.background};
    }
`;

const StyledDivider = styled(Divider)`
    margin-bottom: 15px !important;
`;

const inputProps = { disableUnderline: true };

/**
 * Container that is used to display the members sidebar of workspace
 */
class WorkspaceMembersList extends PureComponent {
    state = {
        searchValue: '',
        teamsListKey: Math.random().toString(36).substring(2, 15),
    };

    teamsListRef = React.createRef();

    componentDidMount() {
        this.loadWorkspaceUsers();
    }

    @bind
    onChangeSearch(event) {
        const searchValue = event.target.value;
        this.setState({ searchValue });
        this.debouncedSearch();
    }

    @bind
    @debounce(1000)
    debouncedSearch() {
        const workspaceId = getStr(this.props.details, 'id');
        if (!workspaceId) return;
        this.setState({ teamsListKey: Math.random().toString(36).substring(2, 15) });
    }

    @bind
    async onSubmitTeams(teams) {
        const id = getStr(this.props, 'details.id', '');
        if (!id || isEmpty(teams)) {
            return;
        }
        const teamInput = teams.map(({ id, role }) => ({ teamId: id, role }));
        await this.props.addWorkspaceTeams(id, teamInput);
        this.setState({ teamsListKey: Math.random().toString(36).substring(2, 15) });
    }
    @bind
    async updateRole(role, payload, type) {
        const id = getStr(this.props, 'details.id', '');
        if (!id || role === payload.role || !payload?.id) {
            return;
        }
        const { addWorkspaceTeams, addWorkspaceUsers } = this.props
        if(type === 'user'){
            const userInput = [{ role, userId: payload.id }];
            await addWorkspaceUsers(id, userInput, 'User role updated successfully.');
            this.loadWorkspaceUsers();
            return;
        }
        const teamInput = [{ role, teamId: payload.id }];
        await addWorkspaceTeams(id, teamInput, 'Team role updated successfully.');
        this.teamsListRef?.current?.updateItemData(payload.id, { role });
    }

    @bind
    async removeWorkspaceTeams(selectedTeamsListIds) {
        const id = getStr(this.props, 'details.id', '');
        if (!id || isEmpty(selectedTeamsListIds)) {
            return;
        }
        await this.props.removeWorkspaceTeams(id, selectedTeamsListIds);
        this.setState({ teamsListKey: Math.random().toString(36).substring(2, 15) });
    }

    @bind
    async onSubmitUsers(users) {
        const id = getStr(this.props, 'details.id', '');
        if (!id || isEmpty(users)) {
            return;
        }
        const userInput = users.map(({ id, role }) => ({ userId: id, role }));
        await this.props.addWorkspaceUsers(id, userInput);
        this.loadWorkspaceUsers();
    }

    @bind
    async removeWorkspaceUsers(selectedUsersListIds) {
        const id = getStr(this.props, 'details.id', '');
        if (!id || isEmpty(selectedUsersListIds)) {
            return;
        }
        await this.props.removeWorkspaceUsers(id, selectedUsersListIds);
        this.loadWorkspaceUsers();
    }

    @bind
    @memoize()
    canEdit(owners) {
        const { id: userId, isAdmin } = this.props.profile;
        if (isAdmin) {
            return true;
        }
        return !!(owners || []).find(({ id }) => id === userId);
    }
    @bind
    @memoize()
    filterSearchedData(searchQuery, data) {
        return searchQuery ? (data || []).filter(item => this.searchForFrom(searchQuery, item)) : data || [];
    }

    @bind
    searchForFrom(searchQuery, data) {
        if (!data || !searchQuery) return false;
        return ['name', 'id', 'username', 'primary.username'].some(field => {
            let value = getStr(data, field, '');
            if (!value) return false;
            value = value.toLowerCase();
            const query = searchQuery.toLowerCase();
            return value.includes(query);
        });
    }

    @bind
    @memoize()
    loadWorkspaceTeams(options) {
        const { startIndex = 0, stopIndex = 10, filterBy } = options || {};
        const { searchValue } = this.state;
        
        const id = getStr(this.props, 'details.id', '');
        if (!id) {
            return Promise.resolve();
        }
        return this.props.loadWorkspaceResources(id, 'team', { startIndex, stopIndex, filterBy, searchValue });
    }

    @bind
    loadWorkspaceUsers() {
        const id = getStr(this.props, 'details.id', '');
        if (!id) {
            return Promise.resolve();
        }
        return this.props.loadWorkspaceUsers(id);
    }

    render() {
        const { details, users, teams, teamsCount, teamsLoading, teamsStartIndex } = this.props;
        const { owners } = details || {};
        const { searchValue, teamsListKey } = this.state;
        const isDisabled = !this.canEdit(owners);
        const isOwner = this.canEdit(owners);
        const filteredUsers = this.filterSearchedData(searchValue, users);
        return (
            <>
                <StyledGrid>
                    <SearchField
                        onChange={this.onChangeSearch}
                        value={searchValue}
                        fullWidth
                        margin="none"
                        placeholder="Search..."
                        InputProps={inputProps}
                    />
                    <StyledDivider />
                </StyledGrid>
                <SelectionList
                    key={teamsListKey}
                    expanded
                    root
                    list={teams || []}
                    type="team"
                    title="Workspace teams"
                    onAdd={this.onSubmitTeams}
                    onRemove={this.removeWorkspaceTeams}
                    disabled={isDisabled}
                    userRole={isOwner ? 'editor' : null}
                    setRole={(role, team) => this.updateRole(role, team, 'team')}
                    showRoles
                    lazy
                    loadData={this.loadWorkspaceTeams}
                    isLoading={teamsLoading}
                    startIndex={teamsStartIndex || 0}
                    itemCount={teamsCount || 0}
                    workspaceId={getStr(details, 'id', '')}
                    ref={this.teamsListRef}
                />
                <SelectionList
                    list={filteredUsers || []}
                    root
                    type="user"
                    title="Workspace users"
                    onAdd={this.onSubmitUsers}
                    onRemove={this.removeWorkspaceUsers}
                    disabled={isDisabled}
                    userRole={isOwner ? 'editor' : null}
                    setRole={(role, user) => this.updateRole(role, user, 'user')}
                    showRoles
                />
            </>
        );
    }
}

export default connect(
    (state, props) => ({
        profile: state.user.profile,
        teamsLoading: state.admin.workspaces.lazyResources.team?.isLoading,
        teams: state.admin.workspaces.lazyResources.team?.data?.records,
        teamsCount: state.admin.workspaces.lazyResources.team?.data?.count,
        teamsStartIndex: state.admin.workspaces.lazyResources.team?.data?.startIndex,
        usersLoading: state.admin.workspaces.users.isLoading,
        users: state.admin.workspaces.users.data?.records,
    }),
    {
        addWorkspaceTeams,
        removeWorkspaceTeams,
        addWorkspaceUsers,
        removeWorkspaceUsers,
        loadWorkspaceResources,
        loadWorkspaceUsers,
    }
)(WorkspaceMembersList);
